import {Component, DestroyRef, inject, Input, OnDestroy, OnInit} from '@angular/core';
import {BokkzIbDataService, IncomeTaxEntity, OmschrijvingEnBedrag} from '@snelstart-ventures/fiscaal/domain-logic';
import {FormArray, FormControl, FormGroup} from '@angular/forms';
import {
  debounceTime,
  distinctUntilChanged,
  filter,
  map,
  Observable,
  shareReplay,
  Subscription,
  switchMap,
  take,
  tap
} from 'rxjs';
import {SharedUiFormControlsModule} from '@snelstart/ui-form-controls';
import {SharedUiButtonModule} from '@snelstart/ui-button';
import {SharedUiDetailsModule} from '@snelstart/ui-details';
import {CommonModule} from '@angular/common';
import {takeUntilDestroyed} from '@angular/core/rxjs-interop';

type MedicijnenFormGroup = FormGroup<{
    omschrijving: FormControl<string | null>;
    bedrag: FormControl<number | null>;
}>;

@Component({
    selector: 'snelstart-venture-fiscaal-feature-medicijnen',
    templateUrl: './medicijnen.component.html',
    styleUrls: ['./medicijnen.component.scss'],
    imports: [SharedUiFormControlsModule, SharedUiButtonModule, SharedUiDetailsModule, CommonModule],
})
export class MedicijnenComponent implements OnInit, OnDestroy {

  @Input() public id!: string;
    private dataService = inject(BokkzIbDataService);
    private destroyRef = inject(DestroyRef);
    private changeSubscriptions = new Subscription();

    public formGroup = new FormGroup({
        medicijnen: new FormArray<MedicijnenFormGroup>([]),
    });

    readonly incomeTaxEntity$: Observable<IncomeTaxEntity> = this.dataService.incomeTaxEntity$.pipe(
        filter((data): data is IncomeTaxEntity => !!data?.sstRelationId),
        takeUntilDestroyed(this.destroyRef),
        shareReplay({ bufferSize: 1, refCount: true }),
    );

    public ngOnInit(): void {
        this.incomeTaxEntity$.pipe(take(1), takeUntilDestroyed(this.destroyRef)).subscribe((data) => {
            this.updateForm(data.aangifte?.persoonsgebondenAftrek?.pgaSpecifiekeZorgkosten?.medicijnen || []);
        });
        this.subscribeToChanges();
    }

    ngOnDestroy(): void {
        this.changeSubscriptions.unsubscribe();
    }

    private subscribeToChanges(): void {
        this.changeSubscriptions.unsubscribe();
        this.changeSubscriptions = new Subscription();

        const extractRelevant = () =>
            this.medicijnen.getRawValue().map((w) => ({
                omschrijving: w.omschrijving,
                bedrag: w.bedrag,
            }));

        this.changeSubscriptions.add(
            this.medicijnen.valueChanges
                .pipe(
                    debounceTime(500),
                    tap(() => this.saveForm()),
                    map(() => extractRelevant()),
                    distinctUntilChanged((prev, curr) => JSON.stringify(prev) === JSON.stringify(curr)),
                    switchMap(() => this.dataService.calculateLatestIncomeTaxReturn()),
                    takeUntilDestroyed(this.destroyRef),
                )
                .subscribe(),
        );
    }

    get medicijnen(): FormArray {
        return this.formGroup.controls.medicijnen;
    }

    addRij(): void {
        this.medicijnen.push(this.createFormGroup());
    }

    verwijderRij(index: number): void {
        this.medicijnen.removeAt(index, { emitEvent: false });
        this.formGroup.updateValueAndValidity({ emitEvent: false });
        this.formGroup.markAsDirty({ emitEvent: false });
        this.saveForm();
    }

    createFormGroup(medicijnen?: OmschrijvingEnBedrag): FormGroup {
        return new FormGroup({
            omschrijving: new FormControl<string | null>(medicijnen?.omschrijving ?? null),
            bedrag: new FormControl<number | null>(medicijnen?.bedrag ?? null),
        });
    }

    private updateForm(data: OmschrijvingEnBedrag[]): void {
        const lijst = data || [];
        const formArray = this.medicijnen;

        while (formArray.length > lijst.length) {
            formArray.removeAt(formArray.length - 1, { emitEvent: false });
        }

        for (let i = 0; i < lijst.length; i++) {
            if (i < formArray.length) {
                const control = formArray.at(i) as FormGroup;
                control.patchValue(lijst[i], { emitEvent: false });
            } else {
                const formGroup = this.createFormGroup(lijst[i]);
                formArray.push(formGroup, { emitEvent: false });
            }
        }
    }

    saveForm(): void {
        const medicijnen = this.formGroup
            .getRawValue()
            .medicijnen.filter((restant) => restant.omschrijving || restant.bedrag)
            .map(
                (verplichtingen) =>
                    ({
                        omschrijving: verplichtingen.omschrijving,
                        bedrag: verplichtingen.bedrag,
                    }) as OmschrijvingEnBedrag,
            );

        this.dataService.patchIncomeTaxEntity((currentEntity) => {
            if (!currentEntity.aangifte) {
                return currentEntity;
            }

            return {
                ...currentEntity,
                aangifte: {
                    ...currentEntity.aangifte,
                    persoonsgebondenAftrek: {
                        ...currentEntity.aangifte.persoonsgebondenAftrek,
                        pgaSpecifiekeZorgkosten: {
                            ...currentEntity.aangifte.persoonsgebondenAftrek?.pgaSpecifiekeZorgkosten,
                            medicijnen: medicijnen,
                        },
                    },
                },
            } as IncomeTaxEntity;
        });
    }
}
